import { configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import { authReducer } from "features/auth";
import { default as breadcrumbsReducer } from "features/breadcrumbs/slice";
import { infoReducer } from "features/info";
import { default as notificationsReducer } from "features/notifications/slice";
import { pageHeaderReducer } from "features/page-header";
import { default as settingsReducer } from "features/settings/slice";
import { timeReducer } from "features/time";
/* PLOP_IMPORT_REDUCERS */
import { draftWizardReducer } from "features/draft-wizard";
import { activityLogSplitPanelReducer } from "features/activity-log-split-panel";
import { filePreviewSplitPanelReducer } from "components/file-preview-split-panel";
import { default as dataCreateReducer } from "features/data-create/slice";
import {
  uiContentReducer,
  litigationUiContentReducer,
  noticeUiContentReducer,
} from "features/ui-content";
import { auditsReducer } from "features/audits";
import { gaiaObjectsReducer } from "features/gaiaObjects";
import { teamSettingsReducer } from "features/team-settings";
import { localApi } from "services/local";
import { mainApi } from "services/main";
import { adiApi } from "services/adi";
import { default as approvalReducer } from "features/approval/slice";
import { assistantReducer } from "features/assistant";
import { atlasChatServiceApi } from "services/atlas-chat";
import { default as commentsPendingReducer } from "components/comments/pendingSlice";

export const store = configureStore({
  reducer: {
    auth: authReducer,
    breadcrumbs: breadcrumbsReducer,
    info: infoReducer,
    notifications: notificationsReducer,
    pageHeader: pageHeaderReducer,
    settings: settingsReducer,
    time: timeReducer,
    /* PLOP_INJECT_REDUCERS */
    draftWizard: draftWizardReducer,
    activityLogSplitPanel: activityLogSplitPanelReducer,
    filePreviewSplitPanel: filePreviewSplitPanelReducer,
    approval: approvalReducer,
    assistant: assistantReducer,
    dataCreate: dataCreateReducer,
    uiContent: uiContentReducer,
    litigationUiContent: litigationUiContentReducer,
    noticeUiContent: noticeUiContentReducer,
    audits: auditsReducer,
    gaiaObjects: gaiaObjectsReducer,
    teamSettings: teamSettingsReducer,
    commentsPending: commentsPendingReducer,
    [localApi.reducerPath]: localApi.reducer,
    [mainApi.reducerPath]: mainApi.reducer,
    [adiApi.reducerPath]: adiApi.reducer,
    [atlasChatServiceApi.reducerPath]: atlasChatServiceApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // Note: One of the core usage principles for Redux is that you should not put
      // non-serializable values in state or actions.
      // See: https://redux-toolkit.js.org/usage/usage-guide#working-with-non-serializable-data
      // We turn it off to allow the infoReducer actions to accept non-serializable state value
      // to update the AppLayout tools' inner content with a ReactNode.
      serializableCheck: false,
    })
      .concat(localApi.middleware)
      .concat(mainApi.middleware)
      .concat(adiApi.middleware)
      .concat(atlasChatServiceApi.middleware),
});

setupListeners(store.dispatch);

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;

// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
